import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveCoverUrl, withApiPrefix } from '../../services/api';
import styles from './StudyWordsPage.module.css';
import { AlertIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import FavoriteWordsView from './FavoriteWordsView';
import StudyHeader from './StudyHeader';

const StudyWordsPage = () => {
  const navigate = useNavigate();

  const [wordGroups, setWordGroups] = useState([]);
  const [wordGroupsLoading, setWordGroupsLoading] = useState(true);
  const [wordGroupsError, setWordGroupsError] = useState(null);
  const [wordGroupLoadingMap, setWordGroupLoadingMap] = useState({});
  const [bookMetadata, setBookMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const groupedWordIds = useMemo(() => {
    const ids = new Set();
    wordGroups.forEach((group) => {
      if (group?.bookId != null) {
        ids.add(String(group.bookId));
      }
    });
    return Array.from(ids);
  }, [wordGroups]);

  const fetchWordGroupsSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setWordGroupsError(null);
      setWordGroupsLoading(true);

      const response = await api.get('/favorite-words/summary', {
        params: { limit: 50 },
      });
      const groups = Array.isArray(response.data?.groups) ? response.data.groups : [];
      const normalized = groups.map((group) => ({
        key: String(group.book_id ?? group.bookId ?? 'unknown'),
        bookId: group.book_id ?? group.bookId ?? null,
        bookTitle: group.book_title || group.bookTitle || '未命名书籍',
        itemCount: group.item_count ?? group.itemCount ?? 0,
        items: [],
      }));

      setWordGroups(normalized);
    } catch (err) {
      console.error('获取收藏单词统计失败:', err);
      setWordGroupsError('获取收藏单词失败，请稍后重试');
      setError('获取收藏单词失败，请稍后重试');
    } finally {
      setWordGroupsLoading(false);
      setLoading(false);
    }
  }, []);

  const fetchBookMetadata = useCallback(async (bookIds) => {
    if (!bookIds.length) {
      setBookMetadata({});
      return;
    }

    const responses = await Promise.allSettled(
      bookIds.map(async (novelId) => {
        const response = await api.get(`/novels/${novelId}`);
        return { novelId, data: response.data };
      })
    );

    const meta = {};
    responses.forEach((result) => {
      if (result.status === 'fulfilled') {
        const key = String(result.value.novelId);
        const data = result.value.data || {};
        meta[key] = {
          ...data,
          cover_image_url: resolveCoverUrl(data.cover_image_url),
        };
      }
    });
    setBookMetadata(meta);
  }, []);

  useEffect(() => {
    fetchWordGroupsSummary();
  }, [fetchWordGroupsSummary]);

  useEffect(() => {
    if (!groupedWordIds.length) {
      return;
    }
    fetchBookMetadata(groupedWordIds);
  }, [groupedWordIds, fetchBookMetadata]);

  const handleOpenBookWords = (bookGroup) => {
    if (!bookGroup?.bookId) {
      return;
    }

    const bookMeta = bookMetadata[String(bookGroup.bookId)] || {};

    const goDetail = (items) => {
      navigate(`/study/words?bookId=${encodeURIComponent(bookGroup.bookId)}`, {
        state: {
          bookGroup: {
            key: String(bookGroup.key),
            bookId: bookGroup.bookId,
            bookTitle: bookMeta.title || bookGroup.bookTitle || '未命名书籍',
            items: items ?? bookGroup.items,
            itemCount: bookGroup.itemCount,
          },
          bookMeta,
        },
      });
    };

    if (!bookGroup?.items?.length) {
      if (wordGroupLoadingMap[bookGroup.key]) {
        return;
      }
      setWordGroupLoadingMap((prev) => ({ ...(prev || {}), [bookGroup.key]: true }));
      setWordGroupsLoading(true);
      api
        .get(withApiPrefix(`favorite-words/by-book/${bookGroup.bookId}`))
        .then((response) => {
          const items = Array.isArray(response.data) ? response.data : [];
          setWordGroups((prev) =>
            prev.map((group) =>
              group.key === bookGroup.key ? { ...group, items } : group
            )
          );
          goDetail(items);
        })
        .catch((err) => {
          console.error('加载收藏单词失败:', err);
          setWordGroupsError('加载收藏单词失败，请稍后重试');
        })
        .finally(() => {
          setWordGroupLoadingMap((prev) => {
            const next = { ...(prev || {}) };
            delete next[bookGroup.key];
            setWordGroupsLoading(Object.keys(next).length > 0);
            return next;
          });
        });
      return;
    }

    goDetail(bookGroup.items);
  };

  if (loading) {
    return (
      <div className={styles.study}>
        <LoadingSpinner fullPage text="加载收藏内容中..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.study}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>无法加载收藏内容</h3>
          <p>{error}</p>
          <button
            className={styles.retryBtn}
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.study}>
      <div className={styles.detailView}>
        <StudyHeader title="收藏单词" onBack={() => navigate('/study')} />
        <div className={styles.detailContent}>
          <FavoriteWordsView
            groups={wordGroups}
            bookMetadata={bookMetadata}
            onOpenBookWords={handleOpenBookWords}
          />
        </div>
      </div>
    </div>
  );
};

export default StudyWordsPage;

